import { NavLink } from 'react-router-dom';
import { Facebook, Instagram } from 'lucide-react';
import { HOST_MB } from '@/lib/constants';
import labelExcellence from '@/assets/images/label_excellence.png';
import amGestionLogo from '@/assets/images/am-gestion.png';
import { useContractsData } from '@/hooks/api/use-contracts';
import { useIsFleetUser } from '@/hooks/api/use-user';
import { useWhitelabel } from '@/hooks/api/use-whitelabel';
import useIsPWA from '@/hooks/use-is-pwa';
import { getApporteurNomLastContract, hasContractApporteur43397 } from '@/lib/contracts';

const Footer = () => {
  const logo = useWhitelabel().data?.logo_dark_url;
  const contracts = useContractsData();
  const hasApporteur43397 = hasContractApporteur43397(contracts);
  const apporteurNomLastContract = getApporteurNomLastContract(contracts);
  const isFleetUser = useIsFleetUser();
  const isPWA = useIsPWA();
  const isAmGestion = HOST_MB !== 43397;
  // Footer affiché pour les clients ayant au moins un contrat apporteur 43397
  // ou pour la marque AM Gestion
  if (!hasApporteur43397 && !isAmGestion) return null;

  const footerLogo = HOST_MB === 43397 ? logo : amGestionLogo;
  // Le nom de la marque n'est pas affiché pour les clients sans contrat 43397
  const showApporteurName = isAmGestion && hasApporteur43397 && !!apporteurNomLastContract;

  const amGestionLinks = [
    { href: 'https://www.amgestionassurance.com/#mentions-legales', label: 'Mentions légales' },
    { href: 'https://www.amgestionassurance.com/#politique-donnees-personnelles-cookies', label: 'Politique de données personnelles' },
    { href: 'https://www.amgestionassurance.com/#cookies', label: 'Gestion des cookies' },
  ];

  const socialLinks = [
    {
      name: 'Facebook',
      href: 'https://www.facebook.com/APRIL.Assurances.Moto/',
      icon: Facebook,
    },
    {
      name: 'Instagram',
      href: 'https://www.instagram.com/aprilmoto/?hl=fr',
      icon: Instagram,
    },
  ];

  const navLinks = [
    { to: '/mes-questions', label: 'FAQ' },
    { to: '/mentions-legales', label: 'Mentions légales' },
    { to: '/gestion-des-cookies', label: 'Gestion des cookies' },
    { to: '/plan-du-site', label: 'Plan du site' },
  ].filter(link => !isFleetUser || link.to !== '/plan-du-site');

  return (
    <>
      {/* Label Excellence */}
      {HOST_MB === 43397 && hasApporteur43397 && (
        <section className="py-8" aria-label="Distinction">
          <div className="max-w-7xl mx-auto px-6 flex justify-center">
            <img
              src={labelExcellence}
              alt="Label Excellence 2025 - Moto et Scooter"
              className="h-36 md:h-48 w-auto object-contain"
            />
          </div>
        </section>
      )}

      {/* En mode app mobile, la BottomNav fixe (h-16 + safe-area) recouvre le bas
          du footer : on compense avec un padding equivalent (masque en lg ou la nav disparait) */}
      <footer className={`bg-foreground text-background ${isPWA ? 'pb-[calc(5.5rem+env(safe-area-inset-bottom))] lg:pb-6' : 'pb-6'}`}
        style={{ marginTop: HOST_MB === 43397 ? 0 : '3rem' }}>
      <div className="max-w-7xl mx-auto px-6">
        {HOST_MB === 43397 && (
          <div className="py-8 lg:py-12">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 lg:gap-8">
              {/* Social Links */}
              <div className="flex items-center gap-4">
                <span className="text-sm text-background/70">Suivez-nous</span>
                <div className="flex items-center gap-2">
                  {socialLinks.map((social) => (
                    <a
                      key={social.name}
                      href={social.href}
                      rel="noreferrer"
                      target="_blank"
                      className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-background/10 text-background/80 transition-colors hover:bg-primary-hover hover:text-primary-foreground"
                      aria-label={social.name}
                    >
                      <social.icon className="w-4 h-4" />
                    </a>
                  ))}
                </div>
              </div>

              {/* Navigation Links */}
              <nav className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 lg:gap-8">
                {navLinks.map((link, index) => (
                  <div key={link.to} className="flex items-center gap-6 lg:gap-8">
                    <NavLink
                      to={link.to}
                      className="text-sm text-background/80 transition-colors hover:text-primary no-underline"
                    >
                      {link.label}
                    </NavLink>
                    {index < navLinks.length - 1 && (
                      <span className="hidden lg:block w-1.5 h-1.5 rounded-full bg-primary" />
                    )}
                  </div>
                ))}
              </nav>
            </div>
          </div>
        )}

        {/* Liens légaux AM Gestion */}
        {isAmGestion && (
          <div className="py-8 lg:py-12">
            <nav className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-4 sm:gap-6 lg:gap-8" aria-label="Liens légaux">
              {amGestionLinks.map((link, index) => (
                <div key={link.href} className="flex items-center gap-6 lg:gap-8">
                  <a
                    href={link.href}
                    rel="noreferrer"
                    target="_blank"
                    className="text-sm text-background/80 transition-colors hover:text-primary no-underline"
                  >
                    {link.label}
                  </a>
                  {index < amGestionLinks.length - 1 && (
                    <span className="hidden sm:block w-1.5 h-1.5 rounded-full bg-primary" />
                  )}
                </div>
              ))}
            </nav>
          </div>
        )}

        {/* Logo & Copyright */}
        <div className="py-8 border-t border-background/20">
          <div className="flex flex-col items-center">
            {showApporteurName ? (
              <span className="font-semibold text-base sm:text-lg text-background text-center px-6">
                {apporteurNomLastContract}
              </span>
            ) : footerLogo && (
              HOST_MB === 43397 ? (
                <img
                  src={footerLogo}
                  alt=""
                  aria-hidden="true"
                  className="h-18 w-auto opacity-80 brightness-0 invert"
                />
              ) : hasApporteur43397 ? (
                <div className="bg-white rounded-md px-6 py-4">
                  <img
                    src={footerLogo}
                    alt=""
                    aria-hidden="true"
                    className="h-12 w-auto"
                  />
                </div>
              ) : null
            )}
          </div>
        </div>
      </div>
    </footer>
    </>
  );
};

export default Footer;
